import React, { useEffect, useState } from "react";
import FormStyleSelector from "./FormStyleSelector";
import FormLayoutSelector from "./FormLayoutSelector";
// Submit Button Label Component
const SubmitButtonLabelInput = ({ submitButtonLabel, onLabelChange }) => {
    return (React.createElement("div", null,
        React.createElement("label", { className: "text-sm font-medium text-gray-700 block mb-1.5" }, "Submit Label"),
        React.createElement("input", { type: "text", value: submitButtonLabel, onChange: (e) => onLabelChange(e.target.value), placeholder: "Submit", className: "w-36 px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white" })));
};
// Form Columns Component
const FormColumnsSelector = ({ formColumns, onColumnsChange }) => {
    const columns = [
        { value: '1', label: '1 Col', bars: 1 },
        { value: '2', label: '2 Col', bars: 2 },
        { value: '3', label: '3 Col', bars: 3 },
    ];
    return (React.createElement("div", null,
        React.createElement("label", { className: "text-sm font-medium text-gray-700 block mb-1.5" }, "Columns"),
        React.createElement("div", { className: "flex gap-1.5" }, columns.map(col => {
            const isActive = formColumns === col.value;
            return (React.createElement("button", { key: col.value, className: `px-3 py-1.5 border rounded-lg cursor-pointer transition-all flex items-center gap-1.5 ${isActive
                    ? 'border-orange-500 bg-orange-50 text-orange-700'
                    : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300 hover:bg-gray-50'}`, onClick: () => onColumnsChange(col.value), title: col.label },
                React.createElement("div", { className: "flex gap-0.5" }, Array.from({ length: col.bars }).map((_, i) => (React.createElement("div", { key: i, className: `w-1.5 h-4 rounded-sm ${isActive ? 'bg-orange-400' : 'bg-gray-300'}` })))),
                React.createElement("span", { className: "text-xs font-medium" }, col.value)));
        }))));
};
// Input Modes Component
const InputModesSelector = ({ currentModes, currentLayout, onModesChange }) => {
    const toggleMode = (mode) => {
        const modes = currentModes.includes(mode)
            ? currentModes.filter(m => m !== mode)
            : [...currentModes, mode];
        onModesChange(modes);
    };
    const allModes = [
        { id: 'speech', label: 'Speech' },
        { id: 'camera', label: 'Camera' },
        { id: 'fileUpload', label: 'Upload' },
        ...(currentLayout === 'form' ? [{ id: 'chat', label: 'Chat' }] : []),
    ];
    return (React.createElement("div", null,
        React.createElement("label", { className: "text-sm font-medium text-gray-700 block mb-1.5" }, "Input Modes"),
        React.createElement("div", { className: "flex items-center gap-1.5" }, allModes.map(mode => {
            const isActive = currentModes.includes(mode.id);
            return (React.createElement("button", { key: mode.id, onClick: () => toggleMode(mode.id), className: `px-3 py-1.5 text-xs font-medium rounded-full border transition-all cursor-pointer ${isActive
                    ? 'bg-orange-500 text-white border-orange-500 shadow-sm'
                    : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300 hover:bg-gray-50'}` }, mode.label));
        }))));
};
// Fluid Style Options Component
const FluidStyleOptionsSelector = ({ currentStyle, collapseProgressPanel, defaultMode, onCollapseProgressPanelChange, onDefaultModeChange }) => {
    if (currentStyle !== 'fluid') {
        return null;
    }
    const modes = [
        { value: 'form-chat', label: 'Form + Chat' },
        { value: 'form', label: 'Form' },
        { value: 'chat', label: 'Chat' },
    ];
    return (React.createElement(React.Fragment, null,
        React.createElement("div", null,
            React.createElement("label", { className: "text-sm font-medium text-gray-700 block mb-1.5" }, "Default Mode"),
            React.createElement("div", { className: "flex gap-1.5" }, modes.map(mode => {
                const isActive = defaultMode === mode.value;
                return (React.createElement("button", { key: mode.value, onClick: () => onDefaultModeChange(mode.value), className: `px-3 py-1.5 text-xs font-medium rounded-full border transition-all cursor-pointer ${isActive
                        ? 'bg-orange-500 text-white border-orange-500 shadow-sm'
                        : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300 hover:bg-gray-50'}` }, mode.label));
            }))),
        React.createElement("div", null,
            React.createElement("label", { className: "text-sm font-medium text-gray-700 block mb-1.5" }, "Progress Panel"),
            React.createElement("button", { onClick: () => onCollapseProgressPanelChange(!collapseProgressPanel), className: `px-3 py-1.5 text-xs font-medium rounded-full border transition-all cursor-pointer ${collapseProgressPanel
                    ? 'bg-orange-500 text-white border-orange-500 shadow-sm'
                    : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300 hover:bg-gray-50'}` }, collapseProgressPanel ? 'Collapsed' : 'Expanded'))));
};
// Main Form Layout Panel Component
const FormLayoutPanel = ({ form }) => {
    const currentLayout = form.getValue("layout") || "form";
    const currentStyle = form.getValue("style") || "classic";
    const cardsInmersiveMode = form.getValue("cardsInmersiveMode") || false;
    const [update, setUpdate] = useState(0);
    const currentModes = form.getValue("inputModes");
    const formColumns = form.getValue("formColumns") || "2";
    const submitButtonLabel = form.getValue("submitButtonLabel") || "Submit";
    const showBranding = form.getValue("showBranding");
    const showBrandingReadOnly = form.getConfig("showBranding").readOnly;
    // Fluid-specific options
    const collapseProgressPanel = form.getValue("collapseProgressPanel") || false;
    const defaultMode = form.getValue("defaultMode") || "form-chat";
    useEffect(() => {
        form.onUpdated((data) => {
            setUpdate((update) => update + 1);
        });
    }, [form]);
    return (React.createElement("div", null,
        React.createElement(FormLayoutSelector, { selectedLayout: currentLayout, onLayoutChange: (layout) => {
                form.setValue('layout', layout);
            } }),
        currentLayout === 'form' && (React.createElement("div", { className: "mb-5 border border-gray-200 rounded-xl overflow-hidden shadow-lg" },
            React.createElement("div", { className: "flex justify-between items-center p-3 bg-gradient-to-r from-gray-50 to-white" },
                React.createElement("h3", { className: "font-medium text-gray-700" }, "Form Options")),
            React.createElement("div", { className: "p-5" },
                React.createElement(FormStyleSelector, { selectedStyle: currentStyle, onStyleChange: (style) => {
                        form.setValue('style', style);
                    } }),
                React.createElement("div", { className: "grid grid-cols-2 gap-x-8 gap-y-4 items-end" },
                    React.createElement(FormColumnsSelector, { formColumns: formColumns, onColumnsChange: (columns) => form.setValue('formColumns', columns) }),
                    React.createElement(SubmitButtonLabelInput, { submitButtonLabel: submitButtonLabel, onLabelChange: (label) => form.setValue('submitButtonLabel', label) }),
                    React.createElement("div", { className: "col-span-2" },
                        React.createElement(InputModesSelector, { currentModes: currentModes, currentLayout: currentLayout, onModesChange: (modes) => form.setValue('inputModes', modes) })),
                    React.createElement(FluidStyleOptionsSelector, { currentStyle: currentStyle, collapseProgressPanel: collapseProgressPanel, defaultMode: defaultMode, onCollapseProgressPanelChange: (collapse) => form.setValue('collapseProgressPanel', collapse), onDefaultModeChange: (mode) => form.setValue('defaultMode', mode) })),
                React.createElement("div", { className: "pt-4 mt-4 border-t border-gray-200" },
                    React.createElement("div", { className: "flex items-center" },
                        React.createElement("input", { type: "checkbox", id: "showBrandingChat", checked: showBranding, onChange: e => form.setValue('showBranding', e.target.checked), className: "h-4 w-4 text-orange-500 rounded focus:ring-orange-500", disabled: showBrandingReadOnly }),
                        React.createElement("label", { htmlFor: "showBrandingChat", className: "ml-2 text-sm text-gray-700" }, "Show \"Powered by FluidForm\" branding")),
                    showBrandingReadOnly && (React.createElement("div", { className: "mt-2 ml-6" },
                        React.createElement("p", { className: "text-xs text-gray-500" },
                            React.createElement("a", { href: "/billing", className: "text-orange-500 hover:text-orange-600 underline" }, "Upgrade to Pro"),
                            " to remove branding")))))))));
};
export default FormLayoutPanel;
